import React, { useMemo } from 'react';
import { ActionStylePreset } from '../../types';
import {
  PropertyMetadata,
  AtomicValueWithNesting,
} from '../abstractStyles/types';
import { filterValidPresets } from './presetValidator';
import { findActivePreset } from './presetMatcher';

type StylePresetsProps = {
  presets: ActionStylePreset[];
  metadata: Record<string, PropertyMetadata>;
  currentDecomposed: Record<string, AtomicValueWithNesting[]>;
  onPresetApplied?: (presetId: string) => void;
  errorMessagePrefix?: string;
};

export const StylePresets: React.FC<StylePresetsProps> = ({
  presets,
  metadata,
  currentDecomposed,
  onPresetApplied,
  errorMessagePrefix = 'preset',
}) => {
  // Filter to only show presets that can be applied
  const validPresets = useMemo(
    () => filterValidPresets(presets, metadata),
    [presets, metadata],
  );

  // Auto-detect active preset if not explicitly set
  const activePresetId = useMemo(
    () => findActivePreset(validPresets, currentDecomposed),
    [validPresets, currentDecomposed],
  );

  const handlePresetClick = async (preset: ActionStylePreset) => {
    // Don't re-apply if already active
    if (activePresetId === preset.id) {
      return;
    }

    // Apply the preset values
    try {
      // Notify parent with the preset ID that was applied
      onPresetApplied?.(preset.id);
    } catch (error) {
      console.error(`Failed to apply ${errorMessagePrefix}:`, error);
    }
  };

  // Don't render if no valid presets
  if (validPresets.length === 0) {
    return null;
  }

  return (
    <section
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {validPresets.map((preset) => {
        const Thumbnail = preset.thumbnail;
        const isSelected = activePresetId === preset.id;

        return (
          <span style={{ width: '38px', height: '38px' }} key={preset.id}>
            <Thumbnail
              isSelected={isSelected}
              onClick={() => handlePresetClick(preset)}
            />
          </span>
        );
      })}
    </section>
  );
};
